import { useEffect, useState } from 'react';

/**
 * Hook to detect when browser tab is visible/focused
 * Uses Page Visibility API to pause/resume activities when user switches tabs
 * 
 * @returns {boolean} isVisible - true if tab is currently visible/focused
 * 
 * @example
 * const isVisible = usePageVisibility();
 * 
 * useQuery({
 *   queryKey: ['data'],
 *   queryFn: fetchData,
 *   enabled: isVisible, // Only fetch when tab is visible
 *   refetchInterval: isVisible ? 60000 : false, // Stop interval when hidden
 * });
 */
export function usePageVisibility(): boolean {
  const [isVisible, setIsVisible] = useState<boolean>(!document.hidden);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return isVisible;
}
